import { AddExternalUserGroupForm } from 'components/Drawer/types';
import useDrawer from 'hooks/useDrawer/useDrawer';
import useEnqueueSnackbar from 'hooks/useEnqueueSnackbar';
import useExternalUsers from 'hooks/useExternalUsers/useExternalUsers';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import DrawerTemplate from 'ui/organisms/Drawer/DrawerTemplate';
import AddExternalUserGroup from 'ui/organisms/Drawer/variants/AddExternalUserGroup';
import { formKeyDownHandler } from 'utils/InputOnKeyDown/formKeyDownHandler.util';

const AddExternalUserVariant: React.FC<React.PropsWithChildren> = () => {
  const { translation } = useTranslation();
  const [nameInputValue, setNameInputValue] = useState('');
  const [showValidation, setShowValidation] = useState(false);
  const [isMaximumUserLimitEnable, setIsMaximumUserLimitEnable] = useState<boolean>(false);
  const [maximumUserLimitValue, setMaximumUserLimitValue] = useState<number | null>(null);
  const { register, handleSubmit, setValue } = useForm<AddExternalUserGroupForm>();
  const { hideDrawer } = useDrawer();
  const enqueueSnackbar = useEnqueueSnackbar();
  const handleFetchError = (errorMessage: string) => enqueueSnackbar(errorMessage, { snackbartype: 'error' });
  const {
    addVisitorGroupWithMaxNumberOfMembers,
    addVisitorGroupWithMaxNumberOfMembersMutationLoading,
    externalUsersGroups
  } = useExternalUsers({
    translation,
    handleFetchError
  });
  const [nameInputValidation, setNameInputValidation] = useState<string | undefined>(undefined);
  const [maximumUserLimitInputValidation, setMaximumUserLimitInputValidation] = useState<string | undefined>(undefined);

  const handleNameInputOnChange = (value: string) => {
    setNameInputValue(value);
  };

  const handleIsMaximumUserLimitEnableOnChange = () => {
    setIsMaximumUserLimitEnable(!isMaximumUserLimitEnable);
    if (maximumUserLimitValue === null) setMaximumUserLimitValue(1);
  };

  const handleMaximumUserLimitValueOnChange = (value: number) => {
    setMaximumUserLimitValue(value);
  };

  const onSubmit = async () => {
    setShowValidation(true);
    if (!addVisitorGroupWithMaxNumberOfMembersMutationLoading && nameInputValidation === undefined) {
      await addVisitorGroupWithMaxNumberOfMembers(nameInputValue, maximumUserLimitValue ?? null);
      hideDrawer();
    }
  };

  useEffect(() => {
    if (nameInputValue === '') setNameInputValidation(translation.name_cannot_be_empty);
    else if (externalUsersGroups.some((group) => group.name === nameInputValue))
      setNameInputValidation(translation.group_name_must_be_unique);
    else setNameInputValidation(undefined);
  }, [externalUsersGroups, nameInputValue, translation.group_name_must_be_unique, translation.name_cannot_be_empty]);

  useEffect(() => {
    if (isMaximumUserLimitEnable && maximumUserLimitValue !== null && maximumUserLimitValue < 1)
      setMaximumUserLimitInputValidation(translation.maximum_users_cannot_be_empty);
    else setMaximumUserLimitInputValidation(undefined);
  }, [
    externalUsersGroups,
    isMaximumUserLimitEnable,
    maximumUserLimitValue,
    translation.value_cannot_be_lower_than_current_amount_of_users_in_the_group,
    translation.maximum_users_cannot_be_empty
  ]);

  useEffect(() => {
    setValue('name', nameInputValue);
  }, [nameInputValue, setValue]);

  return (
    <DrawerTemplate
      title={translation.add_group}
      confirmButtonText={translation.add}
      id="drawer"
      onSubmit={handleSubmit(onSubmit)}
      disableConfirmButton={showValidation && nameInputValidation !== undefined}
    >
      <AddExternalUserGroup
        register={register}
        nameInputValue={nameInputValue}
        handleNameInputOnChange={handleNameInputOnChange}
        nameInputValidation={nameInputValidation}
        showValidation={showValidation}
        switchIsMaximumUserLimitEnable={isMaximumUserLimitEnable}
        handleSwitchIsMaximumUserLimitEnableOnChange={handleIsMaximumUserLimitEnableOnChange}
        inputsMaximumUserLimitValue={maximumUserLimitValue}
        inputsMaximumUserLimitValueValidation={maximumUserLimitInputValidation}
        handlesMaximumUserLimitInputOnChange={handleMaximumUserLimitValueOnChange}
        inputOnKeyDown={(event) => formKeyDownHandler(event, onSubmit)}
      />
    </DrawerTemplate>
  );
};
export default AddExternalUserVariant;
